import { useEffect, useState } from "react";
import type { ClinicalNote } from "@dentacore/shared-types";

import { listNotes, removeNote, reviseNote, writeNote } from "../api/patients";
import { useBranchTimezone } from "../api/use-branch-timezone";
import { useCan } from "../auth/use-can";
import { Pager, useFitPages } from "../ui/paging";
import type { Follow } from "../ui/paging";
import { counted, DAY_GAP, DayCard, DayList, groupByDay } from "./by-day";
import styles from "./notes-tab.module.css";
import { useRingAsk } from "./ring-ask";
import type { RingAsk } from "./ring-ask";

/**
 * The Notes tab — the mockup's *Clinical notes* panel.
 *
 * **Nothing here overwrites anything.** Saving an edit calls `PATCH`, which the
 * API turns into a new version with the old one marked superseded; removing a
 * note calls `DELETE`, which hides it. The screen shows the current version of
 * each note and says when a note has been revised, because a clinician reading
 * a corrected note should know it was corrected.
 *
 * The list is ordered by when each note was **first written**, which is what the
 * API returns and not what the rows' own timestamps say — a note corrected
 * months later stays where it belongs in the timeline.
 *
 * **One container per day, the newest note open and the rest one line each**
 * (PF9, 2026-09-27), in pages that fit the window (PF4). A note somebody opens,
 * writes or revises stays open.
 */
export function NotesTab({
  patientId,
  onCount,
  ask,
  onAsked,
}: {
  patientId: string;
  /** Told how many notes the file has, so the tab can say (PF12). */
  onCount?: (count: number) => void;
  /** The quick ring's *New note* (STORY-221): what the New note button does. */
  ask?: RingAsk<"note"> | null;
  /** Told once the ask has been honoured, so it is not honoured again. */
  onAsked?: () => void;
}): React.JSX.Element {
  // Writing and removing are their own permissions (QA third run).
  const can = useCan();
  const writes = can("clinical.notes.write");
  const hides = can("clinical.notes.hide");
  const [notes, setNotes] = useState<ClinicalNote[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [composing, setComposing] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  /** Notes opened or closed by hand, by id; the newest is open otherwise. */
  const [open, setOpen] = useState<ReadonlyMap<string, boolean>>(new Map());
  /** The day of the note last opened or edited, which the pager keeps on screen. */
  const [follow, setFollow] = useState<Follow | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    setNotes(null);
    setError(null);
    setComposing(false);
    setEditing(null);
    setOpen(new Map());

    listNotes(patientId, controller.signal)
      .then((page) => setNotes(page.notes))
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setError(cause instanceof Error ? cause.message : "Could not read this file's notes.");
      });

    return () => controller.abort();
  }, [patientId]);

  useEffect(() => {
    if (notes !== null) onCount?.(notes.length);
  }, [notes, onCount]);

  const days = notes === null ? [] : groupByDay(notes, (note) => note.writtenAt);
  const newestId = notes?.[0]?.id;
  const isOpen = (note: ClinicalNote): boolean =>
    editing === note.id || (open.get(note.id) ?? note.id === newestId);

  const pages = useFitPages({
    keys: days.map((day) => day.key),
    // A closed note is its 44px line; an open one adds its body, about 90px.
    estimate: (index) =>
      Math.max(
        62,
        (days[index]?.rows ?? []).reduce((sum, note) => sum + 44 + (isOpen(note) ? 90 : 0), 0),
      ),
    gap: DAY_GAP,
    resetOn: patientId,
    follow,
  });
  const [start, end] = pages.range;

  const closeForms = (): void => {
    setComposing(false);
    setEditing(null);
    setTitle("");
    setBody("");
  };

  /**
   * Every write re-reads the list rather than patching state in place.
   *
   * A revision changes which id is current and a removal takes a note off the
   * list, so splicing the response into the array would need this component to
   * re-implement the chain rules the server already owns — and the two would
   * disagree the first time either changed. The note a write returns is opened,
   * so what was just saved is what is on screen.
   */
  const apply = (work: () => Promise<ClinicalNote>, keepOpen: boolean): void => {
    setBusy(true);
    setError(null);

    work()
      .then(async (saved) => {
        const page = await listNotes(patientId);
        setNotes(page.notes);
        if (keepOpen) {
          setOpen((current) => new Map(current).set(saved.id, true));
          setFollow({ key: dayKeyOf(saved) });
        }
        closeForms();
      })
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : "That note did not save.");
      })
      .finally(() => setBusy(false));
  };

  /** *New note* — the button's handler, and the quick ring's (STORY-221). */
  const startNote = (): void => {
    if (!writes || busy || composing) return;
    setEditing(null);
    setTitle("");
    setBody("");
    setComposing(true);
    pages.setPage(0);
  };
  // Once the list has loaded, so its load cannot close the composer again.
  useRingAsk(ask, notes !== null, startNote, onAsked);

  const startEdit = (note: ClinicalNote): void => {
    setFollow({ key: dayKeyOf(note) });
    setComposing(false);
    setEditing(note.id);
    setTitle(note.title ?? "");
    setBody(note.body);
  };

  const composer = (onSave: () => void, saveLabel: string): React.JSX.Element => (
    <div className={styles.composer} data-testid="note-composer">
      <input
        className={styles.input}
        value={title}
        onChange={(event) => setTitle(event.target.value)}
        placeholder="Note title — e.g. Post-op review"
        aria-label="Note title"
        data-testid="note-title"
      />
      <textarea
        className={styles.textarea}
        value={body}
        onChange={(event) => setBody(event.target.value)}
        placeholder="What was observed, done and advised"
        aria-label="Note body"
        rows={4}
        data-testid="note-body"
      />
      <div className={styles.composerActions}>
        <button
          type="button"
          className={styles.primary}
          disabled={busy || body.trim() === ""}
          onClick={onSave}
          data-testid="note-save"
        >
          {saveLabel}
        </button>
        <button
          type="button"
          className={styles.secondary}
          disabled={busy}
          onClick={closeForms}
          data-testid="note-cancel"
        >
          Cancel
        </button>
      </div>
    </div>
  );

  return (
    <div className={styles.notes} data-testid="notes-tab">
      <div className={styles.head}>
        <span className={styles.count}>
          {notes !== null && notes.length > 0 && `${counted(notes.length, "note")}, newest first`}
        </span>
        {writes && (
          <button
            type="button"
            className={styles.primary}
            disabled={busy || composing}
            onClick={startNote}
            data-testid="new-note"
          >
            New note
          </button>
        )}
      </div>

      {error !== null && (
        <p className={styles.error} data-testid="notes-error">
          {error}
        </p>
      )}

      {composing &&
        composer(() => apply(() => writeNote(patientId, noteBody(title, body)), true), "Save note")}

      {notes === null && error === null && <p className={styles.quiet}>Reading the notes…</p>}

      {notes !== null && notes.length === 0 && !composing && (
        <p className={styles.empty} data-testid="notes-empty">
          No notes yet — the first note is written at the examination.
        </p>
      )}

      {/*
        **Grouped by the day they were written**, the same container History,
        Imaging and Billing use. A clinical note belongs to a visit, so a flat
        list made "were these written at the same appointment" a question you
        answered by reading five dates. What stays on the row is what differs —
        who wrote it, and whether it has been revised.
      */}
      {days.length > 0 && (
        <div className={styles.area} ref={pages.areaRef}>
          <DayList testId="note-days">
            {days.slice(start, end).map((day) => (
              <DayCard
                key={day.key}
                dayKey={day.key}
                label={day.label}
                count={counted(day.rows.length, "note")}
                measureRef={pages.measure(day.key)}
                flush
              >
                {day.rows.map((note) =>
                  editing === note.id ? (
                    <div
                      key={note.id}
                      className={styles.editingNote}
                      data-testid="note"
                      data-note-id={note.id}
                    >
                      {composer(
                        () =>
                          apply(() => reviseNote(patientId, note.id, noteBody(title, body)), true),
                        "Save revision",
                      )}
                    </div>
                  ) : (
                    <NoteRow
                      key={note.id}
                      note={note}
                      open={isOpen(note)}
                      busy={busy}
                      onToggle={() => {
                        setFollow({ key: day.key });
                        setOpen((current) => new Map(current).set(note.id, !isOpen(note)));
                      }}
                      onEdit={writes ? () => startEdit(note) : undefined}
                      onRemove={
                        hides ? () => apply(() => removeNote(patientId, note.id), false) : undefined
                      }
                    />
                  ),
                )}
              </DayCard>
            ))}
          </DayList>
        </div>
      )}

      <Pager
        page={pages.page}
        count={pages.pages.length}
        onPage={pages.setPage}
        from={notesBefore(days, start) + 1}
        to={notesBefore(days, end)}
        total={notes?.length ?? 0}
        noun="notes"
        testId="notes-pager"
      />
    </div>
  );
}

/** One note: a line with its title and author, and its body when open. */
function NoteRow({
  note,
  open,
  busy,
  onToggle,
  onEdit,
  onRemove,
}: {
  note: ClinicalNote;
  open: boolean;
  busy: boolean;
  onToggle: () => void;
  onEdit?: () => void;
  onRemove?: () => void;
}): React.JSX.Element {
  const timezone = useBranchTimezone();
  return (
    <article
      className={open ? `${styles.note} ${styles.open}` : styles.note}
      data-testid="note"
      data-note-id={note.id}
      data-open={open}
    >
      <button
        type="button"
        className={styles.noteHead}
        aria-expanded={open}
        onClick={onToggle}
        data-card=""
        data-testid="note-toggle"
      >
        <span className={styles.chevron} aria-hidden="true">
          ›
        </span>
        <span className={styles.noteTitle} data-testid="note-heading">
          {note.title ?? "Untitled note"}
        </span>
        <span className={styles.author} data-testid="note-author">
          <span className={styles.authorLabel}>By</span>{" "}
          {/*
            Three different things store a null author: a note the assistant
            took, an imported one, and one whose author has since been deleted.
            Only the last is "no longer on file"; `source` tells them apart, and
            a named author wins over both.
          */}
          {note.authorName ?? (note.source === "WHATSAPP" ? "Chatbot" : "Author no longer on file")}
        </span>
        {/*
          Kept, and out of sight: the day container carries the date, but specs
          and screen readers still ask a note when it was written.
        */}
        <span className={styles.srOnlyDate} data-testid="note-date">
          {formatDate(note.writtenAt, timezone)}
        </span>
      </button>

      {open && (
        <div className={styles.noteBody}>
          <p className={styles.body} data-testid="note-text">
            {note.body}
          </p>
          <div className={styles.noteMeta}>
            {note.revisions > 1 && (
              <span
                className={styles.revised}
                title={`Last revised ${formatDate(note.revisedAt ?? note.writtenAt, timezone)}. The original is kept.`}
                data-testid="note-revised"
              >
                Revised · {note.revisions} versions
              </span>
            )}
            {onEdit !== undefined && (
              <button
                type="button"
                className={styles.link}
                disabled={busy}
                onClick={onEdit}
                data-testid="note-edit"
              >
                Edit
              </button>
            )}
            {onRemove !== undefined && (
              <button
                type="button"
                className={styles.link}
                disabled={busy}
                onClick={onRemove}
                data-testid="note-delete"
              >
                Remove
              </button>
            )}
          </div>
        </div>
      )}
    </article>
  );
}

/** How many notes sit on the days before `index` — the pager counts notes, not days. */
function notesBefore(days: readonly { rows: readonly unknown[] }[], index: number): number {
  return days.slice(0, index).reduce((sum, day) => sum + day.rows.length, 0);
}

/** The day a note belongs to, as `groupByDay` keys it. */
function dayKeyOf(note: ClinicalNote): string {
  return note.writtenAt.slice(0, 10);
}

/** An empty title is absence, not an empty string — the column is nullable for that. */
function noteBody(title: string, body: string): { title?: string; body: string } {
  const trimmed = title.trim();
  return trimmed === "" ? { body: body.trim() } : { title: trimmed, body: body.trim() };
}

/** `12 Mar 2026`. Locale-aware, and never a raw ISO string in front of a clinician. */
function formatDate(iso: string, timeZone: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? iso
    : // The location's day, not the browser's (P2 Q8).
      date.toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
        timeZone,
      });
}
